import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CAlert, CSpinner, CBadge, CRow, CCol } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilEducation, cilInfo } from '@coreui/icons'
import { get } from '../utils/apiClient'
import { DEGREE_TYPES } from './education/DegreeSelect'
import './EducationManager.css'

const MemberEducationViewer = ({ memberId }) => {
  const { t } = useTranslation()
  const [educations, setEducations] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!memberId) return

    const fetchEducations = async () => {
      setLoading(true)
      try {
        const res = await get(`/members/${memberId}/educations`)
        setEducations(res.data || res)
      } catch (err) {
        console.error('Erreur:', err)
        setError(err.response?.data?.error || err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchEducations()
  }, [memberId])

  const formatYearRange = (startYear, endYear) => {
    if (startYear && endYear) return `${startYear} - ${endYear}`
    if (startYear) return t('education:viewer_since', { year: startYear })
    if (endYear) return t('education:viewer_promotion', { year: endYear })
    return ''
  }

  if (loading) {
    return (
      <div className="tab-content-container">
        <CRow>
          <CCol>
            <div className="text-center py-5">
              <CSpinner color="primary" />
              <p className="mt-3 text-muted">{t('education:viewer_loading')}</p>
            </div>
          </CCol>
        </CRow>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-content-container">
        <CRow>
          <CCol>
            <CAlert color="danger" className="m-3">
              {error}
            </CAlert>
          </CCol>
        </CRow>
      </div>
    )
  }

  if (educations.length === 0) {
    return (
      <div className="tab-content-container">
        <CRow>
          <CCol>
            <div className="empty-state-container">
              <div className="empty-state-icon">
                <CIcon icon={cilEducation} />
              </div>
              <h5>{t('education:viewer_empty_title')}</h5>
              <p className="text-muted">{t('education:viewer_empty_text')}</p>
            </div>
          </CCol>
        </CRow>
      </div>
    )
  }

  return (
    <div className="tab-content-container">
      <CRow>
        <CCol>
          <div className="education-list">
            <div className="experience-count">
              <span>{t('education:viewer_count', { count: educations.length })}</span>
            </div>
            {educations.map((edu, index) => (
              <div
                key={edu.id}
                className={`education-item position-relative mb-4 pb-4 ${index !== educations.length - 1 ? 'border-bottom' : ''}`}
                style={{ '--index': index }}
              >
                <div className="d-flex">
                  <div className="education-icon me-3">
                    <div className="bg-light rounded p-3 d-flex align-items-center justify-content-center">
                      <CIcon icon={cilEducation} size="xl" />
                    </div>
                  </div>

                  <div className="education-content flex-grow-1">
                    <h5 className="mb-1">
                      {edu.school}
                      {edu.school_source === 'official' && (
                        <CBadge
                          color="success"
                          className="ms-2"
                          shape="rounded-pill"
                          style={{ fontSize: '0.65em' }}
                        >
                          {t('education:viewer_verified')}
                        </CBadge>
                      )}
                      {edu.is_primary && (
                        <CBadge color="info" className="ms-2" shape="rounded-pill">
                          {t('education:viewer_primary')}
                        </CBadge>
                      )}
                    </h5>

                    <h6 className="mb-2 text-primary">{edu.program}</h6>

                    <div className="education-details mb-2">
                      {(edu.degree_type || edu.degree) && (
                        <div className="text-muted mb-1">
                          <strong>{t('education:viewer_degree_label')}</strong>{' '}
                          {edu.degree_type
                            ? edu.degree_type === 'other'
                              ? edu.degree_custom || t('education:degree_other')
                              : (() => {
                                  const found = DEGREE_TYPES.find(
                                    (d) => d.value === edu.degree_type,
                                  )
                                  return found ? t(found.labelKey) : edu.degree_type
                                })()
                            : edu.degree}
                        </div>
                      )}
                      {edu.campus && (
                        <div className="text-muted mb-1">
                          <strong>{t('education:viewer_campus_label')}</strong> {edu.campus}
                        </div>
                      )}
                      {(edu.start_year || edu.end_year) && (
                        <div className="text-muted">
                          {formatYearRange(edu.start_year, edu.end_year)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CCol>
      </CRow>
    </div>
  )
}

export default MemberEducationViewer
